import React, { useState, useRef, useEffect } from 'react';
import '../common/css/DateRangePicker.css';

const DateRangePicker = ({ onApply }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenList, setIsOpenList] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [displayText, setDisplayText] = useState('Select Date Range');
  const [selectedRange, setSelectedRange] = useState('custom-date');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDates, setSelectedDates] = useState({ start: null, end: null });
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');
  
  const dropdownRef = useRef(null);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
  
  const daysOfWeek = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatDate = (date) => {
    const day = date.getDate();
    const month = monthNames[date.getMonth()].substring(0, 3);
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleDateClick = (day) => {
    if (!day) return;
    
    const clickedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const formattedDate = formatDate(clickedDate);
    
    if (!selectedDates.start || (selectedDates.start && selectedDates.end)) {
      // Start new selection
      setSelectedDates({ start: day, end: null });
      setTempStartDate(formattedDate);
      setTempEndDate('');
      setSelectedRange('custom-date'); // Set to custom when manually selecting
    } else {
      // Complete the selection
      if (day >= selectedDates.start) {
        setSelectedDates({ ...selectedDates, end: day });
        setTempEndDate(formattedDate);
      } else {
        setSelectedDates({ start: day, end: selectedDates.start });
        setTempEndDate(tempStartDate);
        setTempStartDate(formattedDate);
      }
      setSelectedRange('custom-date'); // Set to custom when manually selecting
    }
  };

  const handleQuickSelect = (option) => {
    const today = new Date();
    let start, end;

    switch(option) {
      case 'Today':
        start = end = today;
        setSelectedRange('today');
        break;
      case 'Weekly':
        start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
        end = today;
        setSelectedRange('weekly');
        break;
      case 'Current Month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = today;
        setSelectedRange('current-month');
        break;
      case 'Yearly':
        start = new Date(today.getFullYear(), 0, 1);
        end = today;
        setSelectedRange('yearly');
        break;
      case 'Custom Date':
        setSelectedRange('custom-date');
        return;
      default:
        return;
    }

    setTempStartDate(formatDate(start));
    setTempEndDate(formatDate(end));
    setSelectedDates({ 
      start: start.getDate(), 
      end: end.getDate() 
    });
    setCurrentMonth(start);
  };

  const isInRange = (day) => {
    if (!day || !selectedDates.start) return false;
    if (!selectedDates.end) return day === selectedDates.start;
    return day >= selectedDates.start && day <= selectedDates.end;
  };

  const isStartDate = (day) => day === selectedDates.start;
  const isEndDate = (day) => day === selectedDates.end;

  const handleApply = () => {
    if (tempStartDate && tempEndDate) {
      setStartDate(tempStartDate);
      setEndDate(tempEndDate);
      setDisplayText(`${tempStartDate} - ${tempEndDate}`);
      
      // Call the onApply callback if provided
      if (onApply) {
        onApply({ startDate: tempStartDate, endDate: tempEndDate });
      }
    }
    setIsOpen(false);
  };

  const handleReset = () => {
    setSelectedDates({ start: null, end: null });
    setTempStartDate('');
    setTempEndDate('');
    setSelectedRange('custom-date');
    setDisplayText('Select Date Range');
    if (onApply) {
        onApply({ startDate: null, endDate: null });
      }
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleToggleList = () => {
    setIsOpenList(!isOpenList);
  };

  const days = getDaysInMonth(currentMonth);

  return (
    <div className="daterangepicker-wrapper" ref={dropdownRef}>
      {/* Trigger Button */}
      <div className="daterangepicker-trigger" onClick={handleToggle}>
        <span className="daterangepicker-display">{displayText}</span>
        <i className={`bx bx-calendar daterangepicker-icon`}></i>
      </div>

      {/* Dropdown Picker */}
      {isOpen && (
        <div className="daterangepicker-dropdown">
          <div className="daterangepicker-container">
            {/* Quick Select Options */}
            <div className="daterangepicker-quick-options">
              {/*['Today', 'Weekly', 'Current Month', 'Yearly', 'Custom Date'].map((option) => {
                const optionValue = option.toLowerCase().replace(' ', '-');
                return (
                  <label key={option} className="daterangepicker-option">
                    <span className="daterangepicker-option-text">{option}</span>
                    <input
                      type="checkbox"
                      checked={selectedRange === optionValue}
                      onChange={() => handleQuickSelect(option)}
                      className="daterangepicker-checkbox"
                    />
                  </label>
                );
              })*/}
              <div className="daterangepicker-hideshow" onClick={handleToggleList}><span className='hideshow-arrowdiv'><i className={`bx bx-chevron-${isOpenList ? "up" : "down"}`}></i></span></div>

              {(isOpenList) && <>

              {['Today', 'Weekly', 'Current Month', 'Custom Date'].map((option) => {
                const optionValue = option.toLowerCase().replace(' ', '-');
                return (
                  <label key={option} className="daterangepicker-option">
                    <span className="daterangepicker-option-text">{option}</span>
                    <input
                      type="checkbox"
                      checked={selectedRange === optionValue}
                      onChange={() => handleQuickSelect(option)}
                      className="daterangepicker-checkbox"
                    />
                  </label>
                );
              })}

              </>}

            </div>

            {(isOpenList) && <div className="daterangepicker-divider"></div> }
            

            {/* Date Inputs */}
            <div className="daterangepicker-inputs">
              <div className="daterangepicker-input-group">
                <label className="daterangepicker-label">Start Date</label>
                <input
                  type="text"
                  value={tempStartDate}
                  onChange={(e) => setTempStartDate(e.target.value)}
                  className="daterangepicker-input"
                  placeholder="DD-MMM-YYYY"
                />
              </div>
              <div className="daterangepicker-input-group">
                <label className="daterangepicker-label">End Date</label>
                <input
                  type="text"
                  value={tempEndDate}
                  onChange={(e) => setTempEndDate(e.target.value)}
                  className="daterangepicker-input"
                  placeholder="DD-MMM-YYYY"
                />
              </div>
            </div>

            {/* Calendar Header */}
            <div className="daterangepicker-calendar-header">
              <button onClick={handlePrevMonth} className="daterangepicker-nav-btn" type="button">
                <i className="bx bx-chevron-left"></i>
              </button>
              <span className="daterangepicker-month-year">
                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </span>
              <button onClick={handleNextMonth} className="daterangepicker-nav-btn" type="button">
                <i className="bx bx-chevron-right"></i>
              </button>
            </div>

            {/* Calendar Grid */}
            <div className="daterangepicker-calendar">
              {/* Day headers */}
              {daysOfWeek.map((day) => (
                <div key={day} className="daterangepicker-day-header">
                  {day}
                </div>
              ))}
              
              {/* Calendar days */}
              {days.map((day, index) => {
                const inRange = isInRange(day);
                const isStart = isStartDate(day);
                const isEnd = isEndDate(day);
                const isSelected = isStart || isEnd;

                return (
                  <div key={index} className="daterangepicker-day-cell">
                    {day ? (
                      <button
                        type="button"
                        onClick={() => handleDateClick(day)}
                        className={`daterangepicker-day ${
                          isSelected ? 'selected' : inRange ? 'in-range' : ''
                        }`}
                      >
                        {day}
                      </button>
                    ) : (
                      <div></div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Action Buttons */}
            <div className="daterangepicker-actions">
              <button
                type="button"
                onClick={handleReset}
                className="daterangepicker-btn daterangepicker-btn-reset"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={handleApply}
                className="daterangepicker-btn daterangepicker-btn-apply"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
